import { SxProps } from '@mui/material';
import { Nullable, TableTypes } from '../../common/types';
import { HubDevice } from '../../rtk/hubDevicesApi';
import { NormalizedLelyUser, UserType } from '@/auth/useLelyAuth';

export const HUB_DEVICES_PANEL_ID = 'Hub';
export const FAVOURITE_HUB_DEVICES_PANEL_ID = 'FavouriteHubDevicesList';
export const NO_FAVOURITE_HUB_DEVICES_MESSAGE = 'No favourite hub devices';
export const MAXIMUM_NUMBER_OF_FAVOURITE_HUB_DEVICES_ALLOWED = 30;
export const MAXIMUM_NUMBER_OF_FAVOURITE_HUB_DEVICES_ERROR_MESSAGE =
  'The maximum amount of favourite devices has been reached.';

export const DYNAMIC_TABLE_HEIGHT_VAR = '--dynamic-table-height';
export const HUB_DEVICES_RESIZEABLE_CLASS = 'hub-devices-resizable';

export const DEFAULT_HUB_DEVICES_SORTING_KEY =
  'serialNumber' as keyof HubDevice;

export const DEFAULT_HUB_DEVICES_LOCAL_STORAGE_KEY = 'hubDevicesListConfig';
export const RING_BUFFER_HELP_SECTION_LOCAL_STORAGE_KEY =
  'ringBufferInfoSection';
export const RING_BUFFER_TOGGLE_TIP_LOCAL_STORAGE_KEY = 'ringBufferToggleTip';

export const HUB_DEVICE_COLUMNS: TableTypes.TableRow[] = [
  {
    title: 'Status',
    key: 'status',
  },
  {
    title: 'Customer name',
    key: 'customerName',
  },
  {
    title: 'Farm name',
    key: 'farmName',
    classNames: 'farmName',
  },
  {
    title: 'Lely center',
    key: 'lelyCenter',
  },
  {
    title: 'Lely cluster',
    key: 'lelyCluster',
  },
  {
    title: 'Customer number',
    key: 'customerNumber',
  },
  {
    title: 'Site id',
    key: 'siteId',
  },
  {
    title: `Link device`,
    key: 'linkAction',
    disableSort: true,
    disableResizing: true,
  },
];

export const CUSTOMIZABLE_HUB_DEVICE_COLUMNS = HUB_DEVICE_COLUMNS.filter(
  (c) => !['favouriteDevice', 'serialNumber'].includes(c.key),
);
export const NON_CUSTOMIZABLE_HUB_DEVICE_COLUMNS = HUB_DEVICE_COLUMNS.filter(
  (c) => ['favouriteDevice', 'serialNumber'].includes(c.key),
);

export const HUB_DEVICE_MESSAGES = {
  LINK_DEVICE:
    'Linking the device to the customer site prepares it for customer use.',
  LINK_DEVICE_SITE_ID:
    'Enter the Site ID to link the device. A Site ID may also be known as "Horizon license key", "license key", or "farm ID".',
  LINK_SUCCESS: 'Device linked successfully.',
  LINK_ERROR: 'Device not linked successfully. Please try again.',
  DEVICE_ALREADY_LINKED_ERROR: 'The device is already linked.',

  UNLINK_SUCCESS: 'Device link removed successfully.',
  UNLINK_ERROR: 'Device link was not removed successfully. Please try again.',

  SITE_ID_FORMAT_ERROR: 'Incorrect site id format.',
  SITE_ID_CHAR_LENGTH: 29,
  SITE_ID_PLACEHOLDER: 'XXXXX-XXXXX-XXXXX-XXXXX-XXXXX',
  SITE_ID_INVALID_ERROR: 'Invalid site id.',

  SERIAL_NUMBER_CHAR_LENGTH: 15,
  SERIAL_NUMBER_PLACEHOLDER: 'XXXXXXXXXXXXXXX',
  SERIAL_NUMBER_LABEL: 'Enter the serial number',
  SERIAL_NUMBER_FORMAT_ERROR: 'Incorrect serial number format.',
  SERIAL_NUMBER_INVALID_ERROR: 'Invalid serial number.',
  SERIAL_NUMBER_INVALID_OR_NOT_EXIST_ERROR:
    'The serial number is invalid or does not exist.',

  DISTRIBUTION_PARTNER_ID_CHAR_LENGTH: 13,
  DISTRIBUTION_PARTNER_ID_PLACEHOLDER: 'ACCXXXXXXXXXX',
  DISTRIBUTION_PARTNER_ID_FORMAT_ERROR:
    'Incorrect distribution partner id format.',

  ADD_DEVICE_TEXT_CENTER:
    'Enter the serial number of the new device to add it to the device list.',
  ADD_DEVICE_TEXT_CLUSTER:
    'Select a Lely Center and enter the serial number of the new device to add it to the device list.',
  ADD_DEVICE_NOT_ALLOWED_TEXT:
    'You cannot add a new device because this functionality is not available for your account. Only employees of a Lely Center or Lely Cluster are authorized to add new devices.',
  ADD_SUCCESS: 'New device added successfully.',
  ADD_ERROR: 'New device not added successfully. Please try again.',
  ADD_CONFLICT_ERROR: 'The device already has a distribution partner added.',
  ADD_COMMON_VALIDATION_ERROR:
    'Invalid serial number or distribution partner id.',

  REMOVE_DEVICE_SERIAL:
    'Enter the serial number of the device to remove it from the device list.',
  REMOVE_CONFLICT_ERROR:
    'The device does not have a distribution partner added.',

  REMOVE_SUCCESS: 'Device removed successfully.',
  REMOVE_ERROR: 'Device not removed successfully. Please try again.',

  ONLY_CENTER_AND_CLUSTER_EMPLOYEES_ERROR_ALLOWED:
    'Only Lely Center and Cluster employees are allowed to add new devices.',

  CENTERS_ERROR: 'Failed to load centers. Please try reloading the page.',
};

export const SITE_ID_VALIDATION_REGEX =
  /^[A-HJ-NP-Z2-9]{5}(-[A-HJ-NP-Z2-9]{5}){4}$/;

export const SERIAL_NUMBER_VALIDATION_REGEX = /^[A-Z0-9]+$/;

export const DISTRIBUTION_PARTNER_ID_VALIDATION_REGEX = /^ACC\d+$/;

export const HubDeviceDialogContentStyles: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

export const DISTRIBUTION_PARTNER_ID_PREFIX = 'ACC';

export const ADD_DEVICE_ALLOWED_USER_TYPES: UserType[] = ['Center', 'Cluster'];

export const getInitialDistributionPartnerId = (
  user?: Nullable<NormalizedLelyUser>,
) => {
  switch (user?.usertype as UserType) {
    case 'Center':
      return user?.lely_center;

    case 'Cluster':
      return '';

    default:
      return '';
  }
};
